import { getPropertyTypeValues, getSearchCriteriaPropertyTypes } from "../../agent-production/components/savedAgents/helpers";
import { types } from "../../agent-production/constants/savedAgents";
import { getDefaultContactInfoColumns, getMlsRestrictionSettings, getRowDataSortedAndRanked } from "../../agent-production/helpers/agentProductionHelpers";
import { setStateData } from "../../common/helpers/state";
import { agentProductionTerms as apt, modules, TimePeriods } from "../../constants";
import { responseKeys } from "../../constants/service";
import { getPropertyTypes } from "../../service/mlsInfo";
import { getProficiencyMetrics } from "../../service/proficiency-metrics";
import { useCommonStore, useSearchStore } from "../store";
import { agentListsEditModalActions } from "./agentListsEditModalActions";
import { getDashboardRecentActivity, recordAgentListLoad, updateSavedAgentList } from "../../service/service-gateway";
import { initialSavedAgentStore, intialPersistedSavedAgentsView, useSavedAgentsStore } from "./store";
import { buildSearchCriteria } from "../../common/helpers/utilities";
import { useSearchResultsStore } from "../searchResults/store";
import { useAuthStore } from "../auth/store";
import { getOwnerIdByTokenInfo } from "../../constants/auth";
import { showToast, toastTypes } from "../../common/helpers/toast";

export const actions = (set, get) => ({
    ...agentListsEditModalActions(set, get),
    runSelectedList: async (setState, setModuleAgentData, forceRefetch = false) => {
        const savedAgentsModule = modules.proficiencyMetrics.savedAgents;
        const selectedList = get().selectedListByModule[savedAgentsModule]
        const mlsId = selectedList?.mlsId;
        const agentIds = selectedList?.agentIds;
        const agentListId = selectedList?.agentListId;
        if (!selectedList || !mlsId) return;

        const currentSearchResults = useSearchResultsStore.getState()[savedAgentsModule]?.searchResults;
        const isSameMls = currentSearchResults?.[0]?.mlsSid === mlsId;
        const isCacheValid  = currentSearchResults?.length === 0 && agentIds?.length === 0;
        const shouldUseCache = !forceRefetch && Array.isArray(currentSearchResults) && (isSameMls || isCacheValid );

        if (shouldUseCache) {
            const rankedCacheResults = getRowDataSortedAndRanked(currentSearchResults);
            setStateData('agentData', currentSearchResults, setState);
            setModuleAgentData(currentSearchResults);
            setStateData('rowData', rankedCacheResults, setState);
            setStateData('prevSelectedList', selectedList, setState);
            useSearchResultsStore.getState().setSavedAgentsSearchResults(rankedCacheResults);
            return;
        }
        try {
            set({isLoading: true})
            const currentActivitiesMlsId = get().activitiesMlsId;
            const mlsIdString = mlsId?.toString();
            const mlsHasChanged = currentActivitiesMlsId !== mlsIdString;
            if (mlsHasChanged || forceRefetch) {
                set({
                    activities: null,
                    isLoadingActivities: false,
                    activitiesMlsId: mlsHasChanged ? null : currentActivitiesMlsId,
                    activitiesFetchKey: null
                });
                setStateData('activityRowData', [], setState);
            }
            setStateData('selectedAgentsView', types.SHOW_ALL_AGENTS, setState);
            const propertyTypes = await getPropertyTypes(mlsId);
            propertyTypes && useCommonStore.setState({propertyTypes});

            const searchCriteriaPropertyTypes = getSearchCriteriaPropertyTypes(propertyTypes);
            const propertyTypeIds = getPropertyTypeValues(searchCriteriaPropertyTypes, 'typeId');
            const propertyTypeNames = getPropertyTypeValues(searchCriteriaPropertyTypes, 'longName');
            const restrictedMlsSettings = getMlsRestrictionSettings(mlsId);

            set({propertyTypeNames});

            const savedTimePeriod = useSearchStore.getState()[savedAgentsModule]?.loadedSearchCriteria?.timePeriod;
            const timePeriod = { 
                intervalType: savedTimePeriod?.intervalType ?? TimePeriods[apt.last12Months], 
                startDate: savedTimePeriod?.startDate ?? null, 
                endDate: savedTimePeriod?.endDate ?? null, 
            };

            const searchCriteria = buildSearchCriteria({
                mlsId,
                propertyTypeIds,
                propertyTypeNames,
                agentIds,
                listId: agentListId,
                timePeriod,
            });

            setState(prevState => ({
                ...prevState,
                searchCriteria,
                contactInfoColumns: getDefaultContactInfoColumns(restrictedMlsSettings)
            }));

            const {recruitingSoldResponse, recruitingSoldTotals} = await getProficiencyMetrics(searchCriteria);
            if (typeof agentListId === 'number') {
                recordAgentListLoad(agentListId).catch(() => {});
            }
            const rankedResponse = getRowDataSortedAndRanked(recruitingSoldResponse);
            setStateData('agentData', recruitingSoldResponse, setState);
            get().setManualSearchData(recruitingSoldResponse);
            setModuleAgentData(recruitingSoldResponse);
            setStateData('rowData', rankedResponse, setState);
            get().setSavedAgentSearch(searchCriteria);
            setStateData('prevSelectedList', selectedList, setState);
            useSearchResultsStore.getState().setSavedAgentsSearchResults(rankedResponse || []);
            useSearchStore.getState().setTotalsRowData(recruitingSoldTotals, responseKeys.recruitingSoldTotals);
            const selectedView = get().selectedView;
            useSearchStore.getState().setSummaryPinnedData(selectedView.value, responseKeys.recruitingSoldTotals);
        } catch (error) {
            console.log({error})
        } finally {
            set({isLoading: false})
            get().clearListDirtyFlag(agentListId)
        }

    },
    setSavedAgentSearch: (searchCriteria) => {
        const selectedModule = useSearchStore.getState().selectedModule;
        const selectedList = get().selectedListByModule[selectedModule];
        const membership = useCommonStore.getState().membership;
        if(selectedModule !== modules.proficiencyMetrics.savedAgents) return;
        useSearchStore.setState({ isLoading: true });
        try {
            useSearchStore.setState( state => ({
                [selectedModule]: {
                    ...state[selectedModule],
                    search: {
                        ...selectedList,
                        searchCriteria: {
                            ...searchCriteria
                        },
                    },
                    loadedSearchCriteria: searchCriteria.criteria,
                    selectedMls: membership.find(mls => mls.mlsId === selectedList?.mlsId),
                }
            }));
        useSearchStore.setState({ isLoading: false });
        } catch (error) {
            console.log(error);
        }
    },
    resetStore: () => {
        set(initialSavedAgentStore)
    },
    getMlsName: () => {
        const selectedModule = useSearchStore.getState().selectedModule;
        const selectedList = get().selectedListByModule[selectedModule];
        const mlsId = selectedList?.mlsId;
        const membership = useCommonStore.getState().membership;
        return membership.find((p) => p?.mlsId === mlsId)?.shortDescription;
    },
    setManualSearchData: (recruitingSoldResponse) => {
        set({ manualSearchData: recruitingSoldResponse });
    },
    setSelectedListByModule: (module, list) => {
        const prevState = get().selectedListByModule;
        set({ selectedListByModule: { ...prevState, [module]: list }});
    },
    clearSelectedAgentList: () => {
        const prevState = get().selectedListByModule;
        set({ selectedListByModule: { ...prevState, [modules.proficiencyMetrics.agents]: undefined }});
    },
    setGlobalSavedAgentListId: (globalSavedAgentListId) => {
        set({ globalSavedAgentListId });
    },
    getAgentListSelectedValue: (optionGroups) => {
        if (!optionGroups) return undefined;

        const selectedList = get().selectedListByModule[modules.proficiencyMetrics.agents];
        const titleOption = optionGroups?.[0].options[0];

        if (!selectedList) return titleOption;

        return optionGroups?.[1].options.find(option =>
            selectedList.agentListId === option.value
        );
    },
    getSavedAgentsActivities: async (mlsId, timeInterval) => {
        if (!mlsId) return undefined;
        
        const fetchKey = `${mlsId}-${timeInterval.from}-${timeInterval.to}`;
        const currentFetchKey = get().activitiesFetchKey;
        if (currentFetchKey === fetchKey && get().activities !== null) return;
        set({ isLoadingActivities: true });
        try {
            const response = await getDashboardRecentActivity(mlsId, timeInterval);
            set({ 
                activities: response?.activities ?? [],
                activitiesFetchKey: fetchKey,
                activitiesMlsId: mlsId?.toString()
            });
        } catch (error) {
            console.log(error);
        } finally {
            set({ isLoadingActivities: false });
        }
    },
    findAgentListById: () => {
        const searchStore = useSearchStore.getState();
        const commonStore = useCommonStore.getState();
        const selectedModule = searchStore.selectedModule;
        const selectedAgentListId =  searchStore[selectedModule]?.search?.searchCriteria?.criteria?.realEstateDatasourceIdsWithFilters?.[0]?.idFiltering?.[0]?.listId;
        const agentsList = commonStore?.agentLists;
        if(!selectedAgentListId || !agentsList?.length) return;
        return agentsList.find(agentList => agentList.agentListId === selectedAgentListId);
    },
    setSelectedView: selectedView => set({ selectedView }),
    setSavedAgentList: async ({
        savedAgentListIds,
        selectedCount,
        selectedAgentIds,
        setToastProps
    }) => {
        try {
            const { agentLists, getAgentLists } = useCommonStore.getState();
            const requests = savedAgentListIds.map((id) => {
                const agentList = agentLists.find((l) => l.agentListId === parseInt(id));
                const revisedAgentList = {
                    ...agentList,
                    agentIds: [...new Set([...agentList.agentIds, ...selectedAgentIds])]
                };
                useSavedAgentsStore.getState().setSelectedListByModule(revisedAgentList);
                return {
                    promise: updateSavedAgentList(revisedAgentList, true),
                    agentList
                };
            });
            const results = await Promise.all(requests.map((r) => r.promise));

            results.forEach((res, index) => {
                const { agentList } = requests[index];
                const hasError = !res || res.error >= 400;

                if (hasError) {
                    showToast(
                        toastTypes.error,
                        apt.unableToAddAgents(agentList?.name),
                        setToastProps
                    );
                } else {
                    showToast(
                        toastTypes.success,
                        apt.agentsAddedSuccessfully(selectedCount > 1, agentList?.name),
                        setToastProps
                    );
                }
            });
            // Code block to sync agents in Agent Name module when search results are displayed
            const agentNameModule = modules.proficiencyMetrics.agents;
            const agentNameSearch = useSearchStore.getState()[agentNameModule];
            const agentNameListId = agentNameSearch?.search?.searchCriteria?.criteria
                ?.realEstateDatasourceIdsWithFilters?.[0]?.idFiltering?.[0]?.listId;

            if (agentNameListId) {
                results.forEach((res, index) => {
                    const hasError = !res || res.error >= 400;
                    if (hasError) return;
                    const { agentList } = requests[index];
                    if (agentList.agentListId === agentNameListId) {
                        const updatedAgentIds = [...new Set([...agentList.agentIds, ...selectedAgentIds])];
                        useSearchStore.getState().setListInSearchByAgentSearch(updatedAgentIds, agentNameListId);
                    }
                });
            }

            const { tokenType, tokenInfo } = useAuthStore.getState();
            const ownerId = tokenType && getOwnerIdByTokenInfo[tokenType](tokenInfo);
            getAgentLists(ownerId);

        } catch (err) {
            console.error(err);
            showToast(toastTypes.error, apt.unableToAddAgents(), setToastProps);
        }
    },    
    resetPersistedData: () => set(state => ({
        ...state,
        ...intialPersistedSavedAgentsView,
    })),
    markListAsDirty: (listId) => {
        set({
            dirtyListIds: { ...get().dirtyListIds, [listId]: true }
        });
    },
    clearListDirtyFlag: (listId) => {
        const updated = { ...get().dirtyListIds };
        delete updated[listId];
        set({ dirtyListIds: updated})
    },
});
